import { DOMSerializer, Fragment, type Node as ProseMirrorNode, type Slice } from 'prosemirror-model'

import { markdownEditorSchema } from './schema'

/**
 * Clipboard serialization for the artifact editor.
 *
 * The schema's `toDOM` emits the raw citation token (`[ref_…]`) so the markdown
 * round-trip stays lossless, and the readable `[label]` badge only exists in the node
 * view — which ProseMirror bypasses when serializing a copy. Without these overrides,
 * copied text pastes the raw token into Word or an email. Both serializers substitute
 * the readable label; the HTML flavor keeps the marker span's data attributes, so
 * pasting back into a Paxton editor still round-trips into a live marker node.
 */

const defaultDOMSerializer = DOMSerializer.fromSchema(markdownEditorSchema)

export const clipboardSerializer = new DOMSerializer(
  {
    ...defaultDOMSerializer.nodes,
    referenceMarker: (node: ProseMirrorNode) => {
      // The schema's own span (tag + data attributes), with the visible text swapped
      // from the raw token to the badge label the user sees on screen.
      const [tag, attrs] = markdownEditorSchema.nodes.referenceMarker.spec.toDOM!(node) as [string, Record<string, string>, string]
      return [tag, attrs, `[${node.attrs.label}]`]
    },
  },
  defaultDOMSerializer.marks
)

export function serializeClipboardText(slice: Slice): string {
  const content = withReadableMarkers(slice.content)
  return content.textBetween(0, content.size, '\n\n')
}

/** The fragment with every reference marker replaced by its readable `[label]` text. */
function withReadableMarkers(fragment: Fragment): Fragment {
  const children: ProseMirrorNode[] = []
  fragment.forEach((node) => {
    if (node.type === markdownEditorSchema.nodes.referenceMarker) {
      children.push(markdownEditorSchema.text(`[${node.attrs.label}]`))
    } else if (node.childCount > 0) {
      children.push(node.copy(withReadableMarkers(node.content)))
    } else {
      children.push(node)
    }
  })
  return Fragment.fromArray(children)
}
